/**
 * The card every Workforce Explorer panel sits in.
 *
 * `window` is not decoration: several panels are financial-year or
 * point-in-time and deliberately do NOT follow the period picker. Each panel
 * states its own window and basis here so the reader never has to assume the
 * picker applies. A panel with no window line is a bug, not a style choice.
 */
import React, { type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Def, type DefLookup } from './apiContract';

interface PanelShellProps {
  title: string;
  /** Metric-definition key for the heading tooltip. */
  defKey: string;
  def: DefLookup;
  /** Window / basis statement. Always required — see the note above. */
  window: ReactNode;
  right?: ReactNode;
  footnote?: ReactNode;
  className?: string;
  children: ReactNode;
}

export const PanelShell: React.FC<PanelShellProps> = ({
  title,
  defKey,
  def,
  window: windowNote,
  right,
  footnote,
  className,
  children,
}) => (
  <Card className={cn('overflow-hidden', className)}>
    <CardContent className="p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h2 className="flex items-center text-[15px] font-semibold leading-tight text-heritage-blue">
            {title}
            <Def def={def} k={defKey} />
          </h2>
          <div className="mt-0.5 text-[11px] leading-snug text-gray-500">{windowNote}</div>
        </div>
        {right ? <div className="flex shrink-0 items-center gap-3">{right}</div> : null}
      </div>
      {children}
      {footnote ? <div className="mt-2.5 text-[11px] leading-snug text-gray-500">{footnote}</div> : null}
    </CardContent>
  </Card>
);

/** Uniform per-panel degrade: one dead endpoint never blanks the page. */
export const PanelUnavailable: React.FC<{ what: string }> = ({ what }) => (
  <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2.5 text-[11px] leading-snug text-amber-800">
    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
    <div>
      <strong>{what} could not be loaded.</strong> The rest of the page is unaffected — this panel is
      showing nothing rather than a stand-in figure. Refresh to try again.
    </div>
  </div>
);

export const PanelSkeleton: React.FC<{ height?: number }> = ({ height = 180 }) => (
  <div className="animate-pulse rounded bg-gray-100" style={{ height }} />
);

/** Legend swatch + label + its own definition tooltip. */
export const LegendSwatch: React.FC<{
  color: string;
  label: string;
  defKey: string;
  def: DefLookup;
  faded?: boolean;
}> = ({ color, label, defKey, def, faded }) => (
  <span className={cn('inline-flex items-center gap-1.5 text-[11px] text-gray-600', faded && 'opacity-50')}>
    <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
    {label}
    <Def def={def} k={defKey} />
  </span>
);
